import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { UserCircleIcon, DocumentCheckIcon } from '@heroicons/react/24/solid'
import {
	HomeIcon,
	ClipboardIcon,
	EnvelopeIcon,
	CurrencyDollarIcon,
} from '@heroicons/react/20/solid'
import { BellIcon } from '@heroicons/react/24/outline'
import { AnimatePresence, motion } from 'framer-motion'
import styles from '../../layout.module.scss'
import { useAuthStatus } from '../../../utils'
import { useUser, useServiceConfig } from '../../../hooks'
import { useNotifications } from '../../../hooks/notifications'

// 구단 value를 label로 변환
const getCaddieTeamLabel = (teamValue) => {
	if (!teamValue) return null

	const teamMap = {
		spoeum: '스포이음',
		voicecaddie: '보이스캐디',
		mansujung: '만수정',
		other: '기타',
		// 기존 값이 이미 label인 경우를 위한 역매핑
		스포이음: '스포이음',
		보이스캐디: '보이스캐디',
		만수정: '만수정',
		기타: '기타',
	}

	return teamMap[teamValue] || teamValue
}

// 사용자 타입별 설정 (PC MainSidebar와 동일)
const getUserConfig = (user) => {
	const configs = {
		tourPro: {
			profileImage: user.profile_image || '/images/pro-default.png',
			userTypeText: '투어프로',
			userDetail: user.tourPro?.tour_type,
			additionalButtons: [],
		},
		tourCaddie: {
			profileImage: user.profile_image || '/images/caddie-default-profile.svg',
			userTypeText: '투어캐디',
			userDetail: getCaddieTeamLabel(user.tourCaddie?.caddie_team_name) || null,
			additionalButtons: [],
		},
	}
	return configs[user.userType] || configs.tourPro
}

// 메뉴 설정 (PC MainSidebar와 동일, 오피셜 모드에서는 사전계약관리 숨김)
const getMenuConfig = (userType, mode) => {
	const commonMenus = [
		{ key: 'home', label: '홈', icon: HomeIcon, path: '/home', enabled: true },
		...(mode !== 'OFFICIAL'
			? [
					{
						key: 'precontracts',
						label: '사전계약관리',
						icon: ClipboardIcon,
						path: '/precontracts',
						enabled: true,
					},
				]
			: []),
		{ key: 'offers', label: '제안관리', icon: EnvelopeIcon, path: '/offers', enabled: true },
		{
			key: 'contracts',
			label: '계약관리',
			icon: DocumentCheckIcon,
			path: '/contracts',
			enabled: true,
		},
		{
			key: 'settlements',
			label: '정산관리',
			icon: CurrencyDollarIcon,
			path: '/settlements',
			enabled: true,
		},
	]

	// 투어프로만 공고관리 메뉴 추가
	if (userType === 'tourPro') {
		commonMenus.push({
			key: 'posts',
			label: '공고관리',
			icon: ClipboardIcon,
			path: '/posts/manage',
			enabled: true,
		})
	}

	return commonMenus
}

// 공통 스타일
const commonIconStyle = { width: '18px', height: '18px', color: '#4B4A48' }
const disabledIconStyle = { width: '18px', height: '18px', color: '#b6b6b6' }
const switchIconStyle = { width: '18px', height: '18px', color: '#4B4A48' }
const switchTextStyle = { fontFamily: 'Pretendard-Medium', fontSize: '14px' }

export const MobileMenuDrawer = ({ isOpen, onClose }) => {
	const [auth, handleLogout] = useAuthStatus()
	const { data: user, isLoading } = useUser()
	const { mode } = useServiceConfig()
	const { data: notificationsData } = useNotifications()
	const navigate = useNavigate()
	const { pathname } = useLocation()

	// 안읽은 알림 개수 계산
	const unreadCount = React.useMemo(() => {
		if (!notificationsData) return 0

		const notifications = Array.isArray(notificationsData)
			? notificationsData
			: notificationsData.data || notificationsData.notifications || []

		if (!Array.isArray(notifications)) return 0

		return notifications.filter((notification) => !notification.is_read).length
	}, [notificationsData])

	// 프로필 이미지 렌더링
	const renderProfileImage = (userConfig) => {
		return (
			<div style={{ position: 'relative' }}>
				<img
					src={userConfig.profileImage}
					alt="profile"
					style={{
						width: '48px',
						height: '48px',
						borderRadius: '8px',
						objectFit: 'cover',
						border: '1px solid #E4E4E4',
					}}
					onError={(e) => {
						e.target.style.display = 'none'
					}}
				/>
			</div>
		)
	}

	// 메뉴 아이템 렌더링
	const renderMenuItem = (menu) => {
		const IconComponent = menu.icon
		const isEnabled = menu.enabled
		const isActive = pathname === menu.path

		// 활성화된 메뉴의 아이콘 스타일
		const activeIconStyle = { width: '18px', height: '18px', color: '#085e4b' }
		const iconStyle = isActive
			? activeIconStyle
			: isEnabled
				? commonIconStyle
				: disabledIconStyle

		// 활성화된 메뉴의 클래스명
		const activeClassName = isActive ? `${styles.option} ${styles.active}` : styles.option
		const className = isEnabled
			? activeClassName
			: `${activeClassName} ${styles.disabledOption}`

		const handleClick = isEnabled
			? () => {
					navigate(menu.path)
					onClose()
				}
			: (e) => e.preventDefault()

		return (
			<span key={menu.key} className={className} onClick={handleClick}>
				<IconComponent style={iconStyle} />
				<span>
					{menu.label}
					{!isEnabled && <span className={styles.comingSoonInline}> 미오픈</span>}
				</span>
			</span>
		)
	}

	const renderContent = () => {
		// 로그인하지 않은 경우 프로필 섹션 표시 안 함
		if (!auth.isAuthentication || isLoading || !user) {
			return (
				<>
					<div className={styles.mobileMenuHeader}>
						<button type="button" className={styles.mobileMenuClose} onClick={onClose}>
							<XMarkIcon />
						</button>
					</div>
					{/* 컨텐츠 영역 */}
					<div className={styles.mobileMenuContentWrapper}>
						{/* 일반 메뉴 섹션 */}
						<div className={styles.mobileMenuSection}>
							<div className={styles.mobileMenuSectionTitle}>일반 메뉴</div>
							<div className={styles.mobileGeneralMenu}>
								<span
									className={`${styles.mobileGeneralMenuItem} ${pathname === '/' ? styles.active : ''}`}
									onClick={() => {
										navigate('/')
										onClose()
									}}>
									스포이음 홈
								</span>
								<span
									className={`${styles.mobileGeneralMenuItem} ${pathname === '/training-program' ? styles.active : ''}`}
									onClick={() => {
										navigate('/training-program')
										onClose()
									}}>
									캐디 아카데미
								</span>
								<span
									className={`${styles.mobileGeneralMenuItem} ${pathname === '/notice' ? styles.active : ''}`}
									onClick={() => {
										navigate('/notice')
										onClose()
									}}>
									공지사항
								</span>
							</div>
						</div>
					</div>
				</>
			)
		}

		const userConfig = getUserConfig(user)
		const menuConfig = getMenuConfig(user.userType, mode)

		return (
			<>
				<div className={styles.mobileMenuHeader}>
					<button type="button" className={styles.mobileMenuClose} onClick={onClose}>
						<XMarkIcon />
					</button>
				</div>

				{/* 컨텐츠 영역 */}
				<div className={styles.mobileMenuContentWrapper}>
					{/* 프로필 박스 */}
					<div className={styles.profileBox}>
						<div className={styles.profileInfo}>
							{renderProfileImage(userConfig)}
							<span className={styles.user}>
								<div className={styles.userType}>
									{userConfig.userTypeText}
									{userConfig.userDetail && ` / ${userConfig.userDetail}`}
								</div>
								<div className={styles.userName}>{user.name}</div>
							</span>
						</div>
						<div className={styles.switchBox}>
							<span
								className={styles.switchLeft}
								onClick={() => {
									navigate('/myinfo')
									onClose()
								}}>
								<UserCircleIcon
									style={{
										...switchIconStyle,
										color: pathname.startsWith('/myinfo')
											? '#085e4b'
											: '#4B4A48',
									}}
								/>
								<span
									style={{
										...switchTextStyle,
										color: pathname.startsWith('/myinfo')
											? '#085e4b'
											: '#1b1b1b',
									}}>
									설정
								</span>
							</span>
							<span
								className={styles.switchRight}
								onClick={() => {
									navigate('/notifications')
									onClose()
								}}
								style={{ cursor: 'pointer' }}>
								<BellIcon
									style={{
										...switchIconStyle,
										color: pathname.startsWith('/notifications')
											? '#085e4b'
											: '#4B4A48',
									}}
								/>
								<span
									style={{
										...switchTextStyle,
										color: pathname.startsWith('/notifications')
											? '#085e4b'
											: '#1b1b1b',
									}}>
									알림
								</span>
								{unreadCount > 0 && (
									<span
										style={{
											backgroundColor: '#085e4b',
											color: 'white',
											borderRadius: '10px',
											padding: '2px 6px',
											fontSize: '10px',
											fontWeight: '600',
											marginLeft: '4px',
											minWidth: '16px',
											textAlign: 'center',
											display: 'inline-flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}>
										{unreadCount > 99 ? '99+' : unreadCount}
									</span>
								)}
							</span>
						</div>
					</div>

					{/* 마이페이지 섹션 */}
					<div className={styles.mobileMenuSection}>
						<div className={styles.mobileMenuSectionTitle}>마이페이지</div>
						<div className={styles.menuBox}>{menuConfig.map(renderMenuItem)}</div>
					</div>

					{/* 일반 메뉴 섹션 */}
					<div className={styles.mobileMenuSection}>
						<div className={styles.mobileMenuSectionTitle}>일반 메뉴</div>
						<div className={styles.mobileGeneralMenu}>
							<span
								className={`${styles.mobileGeneralMenuItem} ${pathname === '/' ? styles.active : ''}`}
								onClick={() => {
									navigate('/')
									onClose()
								}}>
								스포이음 홈
							</span>
							<span
								className={`${styles.mobileGeneralMenuItem} ${pathname === '/training-program' ? styles.active : ''}`}
								onClick={() => {
									navigate('/training-program')
									onClose()
								}}>
								캐디 아카데미
							</span>
							<span
								className={`${styles.mobileGeneralMenuItem} ${pathname === '/notice' ? styles.active : ''}`}
								onClick={() => {
									navigate('/notice')
									onClose()
								}}>
								공지사항
							</span>
						</div>
					</div>
				</div>

				{/* 하단 로그아웃 버튼 */}
				<div className={styles.mobileMenuFooter}>
					<span className={styles.btnGrayBorder} onClick={handleLogout}>
						로그아웃
					</span>
				</div>
			</>
		)
	}

	return (
		<AnimatePresence>
			{isOpen && (
				<div className={styles.mobileMenuOverlay} onClick={onClose}>
					<motion.div
						className={styles.mobileMenuDrawer}
						onClick={(event) => event.stopPropagation()}
						initial={{ x: '100%' }}
						animate={{ x: 0 }}
						exit={{ x: '100%' }}
						transition={{ duration: 0.2, ease: 'easeOut' }}>
						{renderContent()}
					</motion.div>
				</div>
			)}
		</AnimatePresence>
	)
}
